import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface MultiSelectProps {
  options: { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  showSelectAll?: boolean;
  searchPlaceholder?: string;
}

export const MultiSelect = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select...", 
  className,
  showSelectAll = false,
  searchPlaceholder = "Tìm kiếm..."
}: MultiSelectProps) => {
  const [open, setOpen] = useState(false);

  const allSelected = options.length > 0 && value.length === options.length;
  const noneSelected = value.length === 0;

  const handleSelectAll = () => {
    onChange(options.map(o => o.value));
  };

  const handleUnselectAll = () => {
    onChange([]);
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length === options.length) return `Tất cả (${options.length})`;
    return `${value.length} đã chọn`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("justify-between h-9 text-sm", className)}
        >
          <span className="truncate">
            {getDisplayText()}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          
          {showSelectAll && (
            <>
              <div className="flex gap-1 p-2 border-b">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 h-7 text-xs"
                  onClick={handleSelectAll}
                  disabled={allSelected}
                >
                  Chọn tất cả
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 h-7 text-xs"
                  onClick={handleUnselectAll}
                  disabled={noneSelected}
                >
                  Bỏ chọn
                </Button>
              </div>
            </>
          )}
          
          <CommandEmpty>Không tìm thấy.</CommandEmpty>
          <CommandGroup className="max-h-[300px] overflow-y-auto">
            {options.map((option) => (
              <CommandItem
                key={option.value}
                onSelect={() => {
                  const newValue = value.includes(option.value)
                    ? value.filter(v => v !== option.value)
                    : [...value, option.value];
                  onChange(newValue);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value.includes(option.value) ? "opacity-100" : "opacity-0"
                  )}
                />
                <span className="truncate">{option.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
